import { useContext, useState } from "react";
import { useImmer } from "use-immer";
import AppContext from "../../utils/AppContext";
import i18next from "i18next";
import { api } from "../../utils/api";
import useCurrentUser from "../../hooks/useCurrentUser";
import { setLanguage } from "../../locale/localeUtils";

import SettingsNavigation from "./SettingsNavigation";
import ChangePasswordModal from "./ChangePasswordModal";
import ChangeEmailModal from "./ChangeEmailModal";

import { Trans as T } from 'react-i18next';
import Form from 'react-bootstrap/Form';
import Button from '../../common/Button/Button';

import { ReactComponent as GoogleLogo } from "../../assets/icons_svg/google_logo.svg";

import './Account.scss';

export default function Account() {
  const [, dispatch] = useContext(AppContext);
  const { currentUser, updateUser } = useCurrentUser();

  const [lang, setLang] = useState(() => i18next.language);
  const [firstName, setFirstName] = useState(currentUser.first_name || '');
  const [lastName, setLastName] = useState(currentUser.last_name || '');
  const [notifications, setNotifications] = useImmer(currentUser.email_notifications || {});
  const [loading, setLoading] = useState(false);

  const [unsavedChanges, setUnsavedChanges] = useState(false);
  
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showChangeEmail, setShowChangeEmail] = useState(false);

  const changeLanguage = e => {
    const language = e.target.value;

    setLanguage(language);
    setLang(language);

    api.put(`/users/${currentUser.id}/`, {
      settings: { language }
    });
  }

  const onSaveChanges = async () => {
    setLoading(true);

    const data = {
      first_name          : firstName.trim() || currentUser.first_name,
      last_name           : lastName.trim() || currentUser.last_name,
      email_notifications : notifications,
    }

    if (!firstName.trim()) {
      setFirstName(currentUser.first_name);
    }

    if (!lastName.trim()) {
      setLastName(currentUser.last_name);
    }

    try {
      await updateUser(data);

      dispatch({ type: 'globalToast/set', payload: { message: "Changes saved" }});
      setUnsavedChanges(false);
    }
    catch (e) {
      console.log('e:', e);
    }
    finally {
      setLoading(false);
    }
  }

  const onToggleNotification = (field, checked) => {
    setNotifications(draft => {
      draft[field] = checked;
    });
    setUnsavedChanges(true);
  }

  const onChangeFirstName = value => {
    setFirstName(value);
    setUnsavedChanges(true);
  }

  const onChangeLastName = value => {
    setLastName(value);
    setUnsavedChanges(true);
  }

  return (
    <>
      <SettingsNavigation unsavedChanges={unsavedChanges} onSaveChanges={onSaveChanges} loading={loading} />
      <div className="__account">
        <section className="main-content">
          <h2><T>Profile Details</T></h2>

          <div className="account-settings">
            <div className="label"><T>First name</T></div>
            <Form.Control type='text' value={firstName} onChange={e => onChangeFirstName(e.target.value)} maxLength={150} />
            <div className="spacer" />

            <div className="label"><T>Last name</T></div>
            <Form.Control type='text' value={lastName} onChange={e => onChangeLastName(e.target.value)} maxLength={150} />
            <div />

            <div className="label"><T>Language</T></div>
            <div>
              <Form.Group>
                <Form.Select value={lang} onChange={changeLanguage}>
                  <option value="en">English</option>
                  <option value="es">Español</option>
                </Form.Select>
              </Form.Group>
              <div className="input-details">Only your personal language will change, it will not reflect the change in organizations. <a href="#WIP">Learn more.</a></div>
            </div>
            <div />

            { !currentUser.sso_provider && (
              <>
                <div className="label"><T>Password</T></div>
                <Button onClick={() => setShowChangePassword(true)} className="change-password" variant='link'><T>Change password</T></Button>
                <div />
              </>
            )}

            <div className="label"><T>Email</T></div>
            <div>
              <Form.Control type='email' disabled value={currentUser.email} />
              <div className="input-details"><T>All notifications and updates will be sent to this email.</T></div>
              { currentUser.sso_provider && <div className="sso-login"><GoogleLogo /><div>Your login is managed by <strong>{ currentUser.sso_provider } SSO.</strong></div></div> }
            </div>
            { currentUser.sso_provider
            ? <div />
            : <Button variant="link" className='change-email' onClick={() => setShowChangeEmail(true)}><T>Change email</T></Button>
            }
          </div>

          <h2>Notification Preferences</h2>
          <div className="notification-preferences">
            <Form.Check className="required" disabled type="checkbox" defaultChecked label='Important account & billing communications (required)' />
            <Form.Check type="checkbox" checked={notifications.product} label='Product News & Updates' onChange={e => onToggleNotification('product', e.target.checked)} />
            <Form.Check type="checkbox" checked={notifications.marketing} label='Marketing and communication emails' onChange={e => onToggleNotification('marketing', e.target.checked)} />
          </div>
        </section>

        <div className="account-actions">
          <Button variant='primary' disabled={!unsavedChanges} onClick={onSaveChanges} loading={loading}><T>Save Changes</T></Button>  
          <Button variant='link' className='delete-user' disabled={loading}>Delete Your Account</Button>
        </div>

        <ChangePasswordModal show={showChangePassword} onHide={() => setShowChangePassword(false)} />
        <ChangeEmailModal show={showChangeEmail} onHide={() => setShowChangeEmail(false)} />
      </div>
    </>
  );
}
